import { cloneDeep, filter, find, findIndex, forEach, map, reduce, some, values } from 'lodash-es'
import { v4 as uuidv4 } from 'uuid'

import { ProductBudgetAPI } from 'src/api'
import { toFixedNumber } from 'src/utils/common'

const initOptions = () => ({
  productId: null,
  categoryId: null,
})
const calcPercents = (value, total) => {
  if (!total) {
    return 0
  }
  const percent = (value / total) * 100
  return toFixedNumber(Number(percent), 5)
}
const percentsRound = (percents) => {
  const inaccuracy = 0.00001
  const fixedNumber = toFixedNumber(Number(percents), 5)
  const ceilNumber = Math.ceil(fixedNumber)
  const diff = ceilNumber - fixedNumber
  if (Math.abs(toFixedNumber(diff, 5)) <= inaccuracy) {
    return ceilNumber
  }
  const roundedNumber = Math.round(fixedNumber)
  const diffRound = roundedNumber - fixedNumber
  if (Math.abs(toFixedNumber(diffRound, 5)) <= inaccuracy) {
    return roundedNumber
  }
  return fixedNumber
}

export default {
  namespaced: true,
  state: {
    showModal: false,
    templates: [],
    options: initOptions(),
    categoriesRtb: [],
    expenses: {
      campaigns: [],
    },
    loading: false,
    percentsEditor: {
      enabled: false,
      campaign: null,
      disabled: false,
      budgetType: null,
      useSamePercents: false,
      expenses: {
        dealerships: [],
        total: 0,
      },
    },
  },
  mutations: {
    SET_SHOW_MODAL(state, payload) {
      state.showModal = payload
    },
    SET_LOADING(state, payload) {
      state.loading = payload
    },
    SET_OPTIONS(state, payload) {
      state.options = payload
    },
    SET_EXPENSES(state, payload) {
      state.expenses = payload
    },
    SET_PERCENTS_EDITOR(state, payload) {
      state.percentsEditor = payload
    },
    SET_PERCENTS_EDITOR_DEALERSHIPS(state, payload) {
      state.percentsEditor.expenses.dealerships = payload
    },
    SET_CATEGORIES_RTB(state, payload) {
      state.categoriesRtb = payload
    },
    SET_TEMPLATES(state, payload) {
      state.templates = payload
    },
    UPDATE_EXPENSE(state, { dealershipId, campaign, body }) {
      const dealerships = cloneDeep(state.expenses.dealerships)
      const dealership = find(dealerships, { id: dealershipId })
      const expenses = dealership?.expenses[campaign]
      if (expenses) {
        expenses[0] = {
          ...expenses[0],
          ...body,
        }
        state.expenses.dealerships = dealerships
      }
    },
    UPDATE_PERCENTS_EDITOR_DEALERSHIP(state, { dealershipId, body }) {
      const dealerships = cloneDeep(state.percentsEditor.expenses.dealerships)
      const dealership = findIndex(dealerships, { id: dealershipId })
      if (dealerships[dealership]) {
        dealerships[dealership] = {
          ...dealerships[dealership],
          ...body,
        }
        state.percentsEditor.expenses.dealerships = dealerships
      }
    },
    UPDATE_PERCENTS_EDITOR_EXPENSE(state, { dealershipId, campaign, body }) {
      const dealerships = cloneDeep(state.percentsEditor.expenses.dealerships)
      const dealership = find(dealerships, { id: dealershipId })
      const expense = dealership?.expenses[campaign]
      dealership.expenses = {
        ...dealership.expenses,
        [campaign]: {
          ...expense,
          ...body,
        },
      }
      state.percentsEditor.expenses.dealerships = dealerships
    },
    UPDATE_PERCENTS_TOTAL_CAMPAIGN(state, { campaign, body }) {
      state.percentsEditor = {
        ...state.percentsEditor,
        totalCampaigns: {
          ...state.percentsEditor.totalCampaigns,
          [campaign]: {
            ...state.percentsEditor.totalCampaigns[campaign],
            ...body,
          },
        },
      }
    },
    UPDATE_TEMPLATE(state, { id, body }) {
      const templates = [...state.templates]
      const templateIndex = findIndex(templates, { id })
      const template = templates[templateIndex]
      if (template) {
        templates[templateIndex] = {
          ...template,
          ...body,
        }
        state.templates = templates
      }
    },
    ADD_TEMPLATE(state, payload) {
      state.templates = [...state.templates, payload]
    },
    ADD_CAMPAIGN(state, { title }) {
      state.expenses.campaigns = [...state.expenses.campaigns, title]
      state.expenses.dealerships = map(state.expenses.dealerships, (dealership) => {
        dealership.expenses = {
          ...dealership.expenses,
          [title]: [
            {
              campaign: title,
            },
          ],
        }
        return dealership
      })
    },
    ADD_EXPENSE(state, { dealershipId, campaign, body }) {
      const dealerships = [...state.expenses.dealerships]
      const dealership = find(dealerships, { id: dealershipId })
      dealership.expenses = {
        ...dealership.expenses,
        [campaign]: [
          {
            id: uuidv4(),
            actual: 0,
            planned: 0,
            ...body,
          },
        ],
      }
      state.expenses.dealerships = dealerships
    },
    ADD_PRODUCT(state, { categoryId, product }) {
      const categoriesRtb = [...state.categoriesRtb]
      const category = find(categoriesRtb, {
        id: categoryId,
      })
      if (category && category.products) {
        category.products.push(product)
        state.categoriesRtb = categoriesRtb
      }
    },
  },
  actions: {
    expenses({ commit, dispatch }, { productId }) {
      dispatch('closePercentsEditor')
      commit('SET_LOADING', true)
      return ProductBudgetAPI.getExpenses({ productId }).then((data) => {
        commit('SET_EXPENSES', data)
        commit('SET_LOADING', false)
      })
    },
    createTemplate({ commit }, { name, percents, type }) {
      const body = {
        name,
        type,
        percents,
      }

      return ProductBudgetAPI.createTemplate({ ...body }).then((data) => {
        commit('ADD_TEMPLATE', data)
      })
    },
    createTemplateCampaign({ dispatch }, { name, percents }) {
      return dispatch('createTemplate', {
        name,
        type: 0,
        percents,
      })
    },
    createTemplateDealership({ dispatch, getters }, { name }) {
      return dispatch('createTemplate', {
        name,
        type: 1,
        percents: getters.dealershipPercents,
      })
    },

    updateTemplate({ getters, commit }, { name, templateId, percents }) {
      const body = {
        name,
        percents,
      }
      commit('UPDATE_TEMPLATE', {
        id: templateId,
        body: {
          name,
          percents: map(percents, (value, key) => {
            const result = {}
            if (getters.isDealershipEditMode) {
              result.campaign = key
            } else {
              result.dealerShipId = Number(key)
            }
            result.percent = value
            return result
          }),
        },
      })

      return ProductBudgetAPI.updateTemplate({ ...body, templateId })
    },
    loadTemplate({ commit, state, getters }, { id }) {
      const template = find(getters.templates, { id })
      const dealerships = cloneDeep(state.percentsEditor.expenses.dealerships)
      if (template.for === 'dealership') {
        const id = state.percentsEditor.dealershipId
        const dealership = find(dealerships, { id })
        forEach(state.expenses.campaigns, (key) => {
          const campaign = find(template.percents, {
            campaign: key,
          })
          if (campaign) {
            const expense = dealership.expenses[key]
            dealership.expenses = {
              ...dealership.expenses,
              [key]: {
                ...expense,
                dealershipPercents: campaign.percent,
              },
            }
          }
        })
      } else {
        const campaign = state.percentsEditor.campaign
        for (const { dealerShipId, percent } of template.percents) {
          const dealership = find(dealerships, { id: dealerShipId })
          if (dealership) {
            if (campaign) {
              const expenses = dealership.expenses
              dealership.expenses = {
                ...expenses,
                [campaign]: {
                  ...expenses[campaign],
                  percents: percent,
                },
              }
            } else {
              const allCampaignTotal = state.percentsEditor.allCampaignTotal
              dealership.totalPercents = percent
              dealership.total = (allCampaignTotal / 100) * percent
            }
          }
        }
      }
      commit('SET_PERCENTS_EDITOR_DEALERSHIPS', dealerships)
    },
    saveCampaign({ state, commit, getters }, payload) {
      const { campaign, budgetType, totalCampaigns } = state.percentsEditor
      const budget = totalCampaigns[campaign].value

      const body = {
        campaign,
        budgetType,
        budget,
        percents: getters.campaignPercents,
      }

      const { productId } = payload.params

      return ProductBudgetAPI.saveCampaign({ ...body, productId }).then((data) => {
        commit('SET_EXPENSES', data)
      })
    },
    saveDealership({ state, getters, commit }, payload) {
      const { dealershipId, budgetType, expenses } = state.percentsEditor

      const dealership = find(expenses.dealerships, {
        id: dealershipId,
      })

      const { productId } = payload.params

      const body = {
        dealerShipId: dealershipId,
        budgetType,
        budget: dealership.total,
        percents: getters.dealershipPercents,
      }

      return ProductBudgetAPI.saveDealership({ ...body, productId }).then((data) => {
        commit('SET_EXPENSES', data)
      })
    },
    saveAll({ state, commit }, payload) {
      const { campaigns } = state.expenses
      const { budgetType, useSamePercents, expenses, totalCampaigns } = state.percentsEditor
      const budget = state.percentsEditor.allCampaignTotal
      const getCampaignPercents = () => {
        if (useSamePercents) {
          return reduce(
            totalCampaigns,
            (acc, campaign, key) => {
              if (key) {
                acc[key] = campaign.percents
              }
              return acc
            },
            {}
          )
        }
        return reduce(
          expenses.dealerships,
          (acc, dealership) => {
            acc[dealership.id] = reduce(
              dealership.expenses,
              (acc, item, campaign) => {
                if (campaign) {
                  acc[campaign] = item.dealershipPercents
                }
                return acc
              },
              {}
            )
            return acc
          },
          {}
        )
      }
      const percents = reduce(
        expenses.dealerships,
        (acc, dealership) => {
          acc[dealership.id] = dealership.totalPercents
          return acc
        },
        {}
      )
      const { productId } = payload.params

      const body = {
        campaigns,
        budgetType,
        budget,
        samePercentsOnCampaigns: Number(useSamePercents),
        percents,
        campaignPercents: getCampaignPercents(),
      }

      return ProductBudgetAPI.saveAll({ ...body, productId }).then((data) => {
        commit('SET_EXPENSES', data)
      })
    },
    copyPlannedToActual({ state, commit }) {
      const { productId } = state.options

      ProductBudgetAPI.copyPlannedToActual({ productId }).then((data) => {
        commit('SET_EXPENSES', data)
      })
    },
    copyActualToPlanned({ state, commit }) {
      const { productId } = state.options
      ProductBudgetAPI.copyActualToPlanned({ productId }).then((data) => {
        commit('SET_EXPENSES', data)
      })
    },
    async openModal({ commit, rootGetters }, { options = {} }) {
      await ProductBudgetAPI.getTemplates().then((data) => {
        commit('SET_TEMPLATES', data)
      })
      commit('SET_SCROLL', false, { root: true })
      commit('SET_SHOW_MODAL', true)
      commit('SET_CATEGORIES_RTB', [...rootGetters['budget/categories']])
      commit('SET_OPTIONS', options)
    },
    closeModal({ commit, dispatch }) {
      commit('SET_OPTIONS', initOptions())
      commit('SET_SCROLL', true, { root: true })
      dispatch('closePercentsEditor')
      commit('SET_SHOW_MODAL', false)
    },
    save({ state, dispatch, rootState }) {
      const { year, dealerGroupId, month } = rootState.settings
      const { productId } = state.options
      const params = {
        year,
        month,
        dealerGroupId,
        productId,
      }
      if (state.percentsEditor.isTotal) {
        if (state.percentsEditor.dealershipId) {
          dispatch('saveDealership', { params })
        } else {
          dispatch('saveAll', { params })
        }
      } else {
        dispatch('saveCampaign', { params })
      }
    },
    setSamePercents({ commit, state }, value) {
      const { percentsEditor } = state
      const { expenses } = percentsEditor
      if (value) {
        const totalCampaigns = reduce(
          percentsEditor.totalCampaigns,
          (acc, item, key) => {
            const dealership = expenses.dealerships[0]
            const campaign = dealership.expenses[key]
            const percents = campaign?.dealershipPercents || 0
            return {
              ...acc,
              [key]: {
                ...item,
                percents,
              },
            }
          },
          {}
        )
        commit('SET_PERCENTS_EDITOR', {
          ...state.percentsEditor,
          totalCampaigns,
          useSamePercents: true,
        })
      } else {
        commit('SET_PERCENTS_EDITOR', {
          ...state.percentsEditor,
          useSamePercents: false,
        })
      }
    },
    updatePercentsEditor({ commit, state }) {
      const { allCampaignTotal } = state.percentsEditor
      const totalNumber = Number(allCampaignTotal) || 0

      const calcValue = (percent, total) => toFixedNumber((total / 100) * percent, 2)
      const dealerships = map(state.percentsEditor.expenses.dealerships, (dealership) => {
        const total = calcValue(dealership.totalPercents, totalNumber)
        return {
          ...dealership,
          total,
        }
      })
      const totalCampaigns = reduce(
        state.percentsEditor.totalCampaigns,
        (acc, item, key) => {
          const percents = Number(item.percents) || 0
          const value = calcValue(percents, totalNumber)
          return {
            ...acc,
            [key]: {
              ...item,
              value,
            },
          }
        },
        {}
      )
      commit('SET_PERCENTS_EDITOR', {
        ...state.percentsEditor,
        totalCampaigns,
        expenses: {
          dealerships,
        },
      })
    },
    openPercentsEditor(
      { commit, state, getters },
      { campaign, budgetType, isTotal = false, dealershipId = null }
    ) {
      /*
        percents = percents expense of campaign total
        dealershipPercents = percents expense of dealership total
        totalPercents = percents dealership total of general total
      */
      const allCampaignTotal = getters.allCampaignTotal[budgetType]
      const dealerships = map(state.expenses.dealerships, (dealership) => {
        const dealershipTotal = find(getters.totalDealerships, {
          id: dealership.id,
        })[budgetType]
        const expenses = reduce(
          dealership.expenses,
          (acc, item, key) => {
            const campaignTotal = getters.totalCampaigns[key][budgetType]
            const expense = item ? item[0] : {}
            const value = expense[budgetType] || 0
            return {
              ...acc,
              [key]: {
                ...expense,
                percents: calcPercents(value, campaignTotal),
                dealershipPercents: calcPercents(value, dealershipTotal),
              },
            }
          },
          {}
        )
        const totalPercents = calcPercents(dealershipTotal, allCampaignTotal)
        return {
          ...dealership,
          total: dealershipTotal,
          totalPercents,
          expenses,
        }
      })
      const useSamePercents = (() => {
        const campaigns = {}
        let isUsedSamePercents = true
        for (const dealership of dealerships) {
          forEach(dealership.expenses, (item, key) => {
            if (campaigns[key]) {
              const isDifferent =
                item.dealershipPercents && item.dealershipPercents !== campaigns[key]
              if (isDifferent) {
                isUsedSamePercents = false
                return false
              }
            } else {
              campaigns[key] = item.dealershipPercents
            }
          })
        }
        return isUsedSamePercents
      })()
      const totalCampaigns = reduce(
        getters.totalCampaigns,
        (acc, item, key) => {
          const value = item[budgetType]
          const percents = calcPercents(value, allCampaignTotal)
          return {
            ...acc,
            [key]: {
              percents,
              value,
            },
          }
        },
        {}
      )
      commit('SET_PERCENTS_EDITOR', {
        ...state.percentsEditor,
        enabled: true,
        campaign,
        budgetType,
        isTotal,
        dealershipId,
        allCampaignTotal,
        totalCampaigns,
        useSamePercents,
        expenses: {
          dealerships,
        },
      })
    },
    closePercentsEditor({ commit }) {
      commit('SET_PERCENTS_EDITOR', {
        enabled: false,
      })
    },
    validatePercentsEditor({ state, commit, getters }, payload = {}) {
      const dealershipsPercents = values(getters.percentsEditorDealershipsPercents)
      const payloadPercents = (() => {
        if (payload.percents === undefined) {
          return 100
        }
        return Number(payload.percents)
      })()
      const percentsArray = [
        ...dealershipsPercents,
        getters.totalDealershipsPercentsSum,
        payloadPercents,
      ]
      const valuesArray = [state.percentsEditor.allCampaignTotal]
      const isInvalid = () => {
        const customValidation = () => {
          if (state.percentsEditor.isTotal) {
            if (getters.isDealershipEditMode) {
              const id = state.percentsEditor.dealershipId
              const total = getters.percentsEditorDealership.total
              return !Number(total) || getters.percentsEditorDealershipsPercents[id] !== 100
            }
            if (state.percentsEditor.useSamePercents) {
              return getters.totalCampaignsPercentsSum !== 100
            }
          } else {
            return getters.totalCampaignPercents !== 100
          }
        }
        const isPercentsInvalid = some(
          percentsArray,
          (value) => (value !== 100 && value !== 0) || isNaN(Number(value))
        )
        const isValuesInvalid = some(valuesArray, (value) => isNaN(Number(value)))
        return isPercentsInvalid || isValuesInvalid || customValidation()
      }
      const disabled = isInvalid()
      if (disabled !== state.percentsEditor.disabled) {
        commit('SET_PERCENTS_EDITOR', {
          ...state.percentsEditor,
          disabled,
        })
      }
    },
    createExpense({ dispatch, state }, { actual = 0, planned = 0, dealerShipId, campaign }) {
      const { options } = state
      return dispatch(
        'expense/create',
        {
          body: {
            productId: options.productId,
            productCategoryId: options.categoryId,
            targetBudget: planned,
            actualBudget: actual,
            campaign,
          },
          userParams: {
            dealerShipId,
          },
        },
        { root: true }
      )
    },
    updateExpense({ dispatch }, { actual = null, planned = null, id }) {
      const body = {}
      if (actual || actual === 0) {
        body.actualBudget = actual
      }
      if (planned || planned === 0) {
        body.targetBudget = planned
      }
      return dispatch(
        'expense/update',
        {
          body: {
            id,
            ...body,
          },
        },
        { root: true }
      )
    },
  },
  getters: {
    /*
      total of campaigns (column)
      returns Object of type:
      {
        campaign: {
          planned: Number
          actual: Number
        }
      }
    */
    totalCampaigns(state) {
      return reduce(
        state.expenses.dealerships,
        (acc, dealership) => {
          forEach(dealership.expenses, (item, key) => {
            const accPlanned = acc[key]?.planned || 0
            const accActual = acc[key]?.actual || 0
            const itemPlanned = item[0]?.planned || 0
            const itemActual = item[0]?.actual || 0
            const planned = toFixedNumber(accPlanned + itemPlanned, 2)
            const actual = toFixedNumber(accActual + itemActual, 2)
            acc[key] = {
              planned,
              actual,
            }
          })
          return acc
        },
        {}
      )
    },
    /*
      total of dealerships (row)
      returns Array
    */
    totalDealerships(state) {
      const reduceExpenses = (expenses) =>
        reduce(
          expenses,
          (acc, item) => {
            const itemPlanned = item[0]?.planned || 0
            const itemActual = item[0]?.actual || 0
            const planned = toFixedNumber(acc.planned + itemPlanned, 2)
            const actual = toFixedNumber(acc.actual + itemActual, 2)
            return {
              planned,
              actual,
            }
          },
          {
            planned: 0,
            actual: 0,
          }
        )
      return state.expenses.dealerships.map((item) => ({
        id: item.id,
        ...reduceExpenses(item.expenses),
      }))
    },
    /*
      sum of dealership percents (row percents)
      returns Object of type:
      {
        id: Number
      }
    */
    percentsEditorDealershipsPercents(state) {
      return reduce(
        state.percentsEditor.expenses.dealerships,
        (acc, dealership) => {
          const { id } = dealership
          forEach(state.expenses.campaigns, (key) => {
            const expense = dealership.expenses[key]
            const dealershipPercents = acc[id] || 0
            const itemPercents = Number(expense?.dealershipPercents) || 0
            acc[id] = percentsRound(dealershipPercents + itemPercents)
          })
          return acc
        },
        {}
      )
    },
    /*
      sum of campaigns percents
      (used, when useSamePercents is on)
      returns Number
    */
    totalCampaignsPercentsSum(state) {
      const result = reduce(
        state.percentsEditor.totalCampaigns,
        (acc, campaign) => acc + Number(campaign.percents),
        0
      )
      return percentsRound(result)
    },
    /*
      sum of dealerships total percents
      returns Number
    */
    totalDealershipsPercentsSum(state) {
      const dealerships = state.percentsEditor.expenses.dealerships
      const result = reduce(dealerships, (acc, item) => acc + Number(item.totalPercents), 0)
      return percentsRound(result)
    },
    /*
      total of rows + total of columns
    */
    allCampaignTotal(state, getters) {
      return reduce(
        getters.totalDealerships,
        (acc, item) => ({
          planned: acc.planned + toFixedNumber(item.planned, 2),
          actual: acc.actual + toFixedNumber(item.actual, 2),
        }),
        {
          planned: 0,
          actual: 0,
        }
      )
    },
    /*
      Selects categories from budget.categoriesList
      which is no in budget.rtb.categories.
      these categories available for adding
    */
    categoriesList(state, getters, rootState) {
      const result = []
      const rtbCategories = state.categoriesRtb
      const categoriesList = rootState.budget.categoriesList
      const getProducts = (category, rtbCategory) => {
        const result = []
        for (const product of category.products) {
          const rtbProduct = find(rtbCategory.products, {
            title: product.name,
          })
          if (!rtbProduct) {
            result.push(product)
          }
        }
        return result
      }
      for (const category of categoriesList) {
        if (category.products[0]) {
          const rtbCategory = find(rtbCategories, { title: category.name })
          if (rtbCategory) {
            const products = getProducts(category, rtbCategory)
            if (products[0]) {
              result.push({ ...category, products })
            }
          } else {
            result.push(category)
          }
        }
      }
      return result
    },
    currentProduct(state) {
      const { categoryId, productId } = state.options
      const category = find(state.categoriesRtb, {
        id: categoryId,
      })
      if (category?.products) {
        return find(category.products, {
          id: productId,
        })
      }
    },
    percentsEditorDealership(state) {
      const id = state.percentsEditor.dealershipId
      if (id) {
        const dealerships = state.percentsEditor.expenses.dealerships
        return find(dealerships, { id })
      }
    },
    /*
      Used when editing campaign percents
    */
    totalCampaignPercents(state) {
      const { campaign, expenses } = state.percentsEditor
      const { dealerships } = expenses
      return dealerships.reduce((acc, item) => {
        const percents = item.expenses[campaign]?.percents || 0
        return percentsRound(acc + Number(percents))
      }, 0)
    },
    campaignPercents(state) {
      const { campaign, expenses } = state.percentsEditor
      const { dealerships } = expenses
      return dealerships.reduce((acc, dealership) => {
        const percents = dealership.expenses[campaign]?.percents
        return {
          ...acc,
          [dealership.id]: percentsRound(+percents || 0),
        }
      }, {})
    },
    dealershipPercents(state) {
      const { dealershipId, expenses } = state.percentsEditor
      const dealership = find(expenses.dealerships, {
        id: dealershipId,
      })
      return reduce(
        dealership.expenses,
        (acc, item, key) => {
          if (!key) {
            return acc
          }
          return {
            ...acc,
            [key]: percentsRound(item?.dealershipPercents || 0),
          }
        },
        {}
      )
    },
    templates(state, getters) {
      const { templates } = state
      if (getters.isDealershipEditMode) {
        return filter(
          templates,
          (template) =>
            template.for === 'dealership' &&
            some(template.percents, (percent) =>
              state.expenses.campaigns.includes(percent.campaign)
            )
        )
      }
      return filter(
        templates,
        (template) =>
          template.for === 'campaign' &&
          some(template.percents, (percent) =>
            find(state.expenses.dealerships, {
              id: percent.dealerShipId,
            })
          )
      )
    },
    isTotalBeforeDealerships(state, getters) {
      return state.percentsEditor.enabled && !getters.isDealershipEditMode
    },
    isDealershipEditMode(state) {
      return state.percentsEditor.isTotal && !!state.percentsEditor.dealershipId
    },
    isUsedSamePercents(state, getters) {
      return (
        state.percentsEditor.isTotal &&
        !getters.isDealershipEditMode &&
        state.percentsEditor.useSamePercents
      )
    },
  },
}
